/**
 * Feito pelo agente netexperts
 * Módulo: AnalyticsObra — Tab Executiva (cruza KPIs já calculados de cada área)
 * Data: 2026-06-14
 */
import { ArchiveBoxIcon, ShoppingCartIcon, ArrowDownCircleIcon, ClockIcon, ArrowsRightLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import { useObraResumo } from '../hooks/useObraAnalytics'
import { formatCurrency, formatDate, formatNumber, formatPercent } from '../../../../utils/obraAnalytics'
import TabState from '../components/TabState'

// Visual de conversão da carteira: anel de progresso + barra recebido x a receber
const CarteiraConversao = ({ recebido, aReceber, conversao }) => {
    const total = (recebido || 0) + (aReceber || 0)
    const pct = Math.max(0, Math.min(100, Number(conversao) || 0))
    const pctRecebido = total > 0 ? (recebido / total) * 100 : 0
    const R = 54
    const C = 2 * Math.PI * R
    const dash = (pct / 100) * C

    return (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <p className="text-sm font-bold text-gray-800">Conversão no período</p>
                    <p className="text-xs text-gray-400">Quanto do que entrou + venceu no período já virou caixa (recebido)</p>
                </div>
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-100">Carteira: {formatCurrency(total)}</span>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="relative shrink-0" style={{ width: 140, height: 140 }}>
                    <svg width="140" height="140" className="-rotate-90">
                        <circle cx="70" cy="70" r={R} fill="none" stroke="#e5e7eb" strokeWidth="14" />
                        <circle
                            cx="70" cy="70" r={R} fill="none" stroke="#0891b2" strokeWidth="14" strokeLinecap="round"
                            strokeDasharray={`${dash} ${C}`}
                        />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl font-extrabold text-gray-900">{formatPercent(pct)}</span>
                        <span className="text-[10px] uppercase tracking-wide text-gray-400">convertido</span>
                    </div>
                </div>

                <div className="flex-1 w-full space-y-4">
                    <div className="w-full h-3 rounded-full bg-amber-100 overflow-hidden flex">
                        <div className="h-full bg-cyan-500" style={{ width: `${pctRecebido}%` }} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-xl bg-cyan-50 border border-cyan-100 p-3">
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-cyan-500" />
                                <span className="text-xs text-gray-500">Recebido</span>
                            </div>
                            <p className="text-base font-bold text-cyan-700 mt-1">{formatCurrency(recebido)}</p>
                        </div>
                        <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                                <span className="text-xs text-gray-500">A receber</span>
                            </div>
                            <p className="text-base font-bold text-amber-700 mt-1">{formatCurrency(aReceber)}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

const AreaResumoCard = ({ titulo, icon: Icon, theme, children, onClick, tag }) => (
    <div className={`rounded-2xl border ${theme.soft} p-4 shadow-sm`}>
        <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-lg ${theme.accent}`}>
                    <Icon className="w-4 h-4 text-white" />
                </div>
                <h3 className={`text-sm font-bold ${theme.text}`}>{titulo}</h3>
                {tag && <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 bg-white/70 border border-gray-200 rounded-full px-2 py-0.5">{tag}</span>}
            </div>
            <button onClick={onClick} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-900 transition-colors">
                Detalhes <ArrowRightIcon className="w-3.5 h-3.5" />
            </button>
        </div>
        <div className="space-y-1.5">{children}</div>
    </div>
)

const Linha = ({ label, value }) => (
    <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">{label}</span>
        <span className="font-bold text-gray-900">{value}</span>
    </div>
)

const ExecutivoTab = ({ obraId, investidor, onNavigate, period }) => {
    const { data, loading, error } = useObraResumo(
        obraId,
        investidor,
        { inicio: period?.inicio, fim: period?.fim },
        { requirePeriod: true }
    )
    if (loading || error || !data) return <TabState loading={loading} error={error} />

    // Resiliência: se alguma área não vier no payload, evita quebrar a renderização
    const estoque = data.estoque || {}
    const vendas = data.vendas || {}
    const recebidos = data.recebidos || {}
    const aReceber = data.aReceber || {}
    const movimentacao = data.movimentacao || {}
    const carteira = data.carteira || {}

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <AreaResumoCard titulo="Estoque" tag="acumulado" icon={ArchiveBoxIcon} theme={{ soft: 'bg-emerald-50 border-emerald-100', text: 'text-emerald-700', accent: 'bg-emerald-500' }} onClick={() => onNavigate('estoque')}>
                    <Linha label="Unidades" value={formatNumber(estoque.total_unidades)} />
                    <Linha label="Vendidas" value={formatNumber(estoque.vendidos)} />
                    <Linha label="% Vendido" value={formatPercent(estoque.pct_vendido)} />
                </AreaResumoCard>

                <AreaResumoCard titulo="Vendas" tag="no período" icon={ShoppingCartIcon} theme={{ soft: 'bg-blue-50 border-blue-100', text: 'text-blue-700', accent: 'bg-blue-500' }} onClick={() => onNavigate('vendas')}>
                    <Linha label="Nº de vendas" value={formatNumber(vendas.num_vendas)} />
                    <Linha label="VGV realizado" value={formatCurrency(vendas.vgv)} />
                    <Linha label="Ticket médio" value={formatCurrency(vendas.ticket_medio)} />
                </AreaResumoCard>

                <AreaResumoCard titulo="Recebidos" tag="no período" icon={ArrowDownCircleIcon} theme={{ soft: 'bg-cyan-50 border-cyan-100', text: 'text-cyan-700', accent: 'bg-cyan-500' }} onClick={() => onNavigate('recebidos')}>
                    <Linha label="Recebido no período" value={formatCurrency(recebidos.total_recebido)} />
                    <Linha label="Conversão no período" value={formatPercent(carteira.conversao)} />
                </AreaResumoCard>

                <AreaResumoCard
                    titulo="A Receber"
                    tag={aReceber.periodo_label || 'período + 12 meses'}
                    icon={ClockIcon}
                    theme={{ soft: 'bg-amber-50 border-amber-100', text: 'text-amber-700', accent: 'bg-amber-500' }}
                    onClick={() => onNavigate('a-receber')}
                >
                    <Linha label="Ainda a receber" value={formatCurrency(aReceber.total)} />
                    <Linha label="Em atraso" value={formatCurrency(aReceber.vencido)} />
                    <Linha label="Inadimplência" value={formatPercent(aReceber.taxa_inadimplencia)} />
                </AreaResumoCard>

                <AreaResumoCard
                    titulo="Movimentação"
                    tag={
                        movimentacao.periodo_label
                        || (period?.inicio && period?.fim
                            ? `${formatDate(period.inicio)} a ${formatDate(period.fim)}`
                            : 'no período')
                    }
                    icon={ArrowsRightLeftIcon}
                    theme={{ soft: 'bg-rose-50 border-rose-100', text: 'text-rose-700', accent: 'bg-rose-500' }}
                    onClick={() => onNavigate('movimentacao')}
                >
                    <Linha label="Receitas" value={formatCurrency(movimentacao.receitas)} />
                    <Linha label="Despesas" value={formatCurrency(movimentacao.despesas)} />
                    <Linha label="Impostos" value={formatCurrency(movimentacao.impostos ?? 0)} />
                    <Linha label="Resultado" value={formatCurrency(movimentacao.resultado)} />
                    <p className="text-[11px] text-gray-400 pt-1 leading-snug">Resultado = DRE financeiro (saldo depois − saldo antes). Receitas, Despesas e Impostos são o detalhe, não a conta do Resultado.</p>
                </AreaResumoCard>
            </div>

            <CarteiraConversao recebido={carteira.recebido} aReceber={carteira.a_receber} conversao={carteira.conversao} />
        </div>
    )
}

export default ExecutivoTab
